/**
 * Clic de metrónomo sintetizado con Web Audio. Cada clic se programa en el reloj de audio y se
 * puede cancelar mientras no haya sonado (pausa, cambio de tempo, bucle).
 */
export interface ClickVoice {
  readonly time: number
  cancel(): void
}

export interface ClickOptions {
  accent: boolean
  /** 0–1. */
  volume?: number
}

export function scheduleClick(
  ctx: BaseAudioContext,
  destination: AudioNode,
  time: number,
  { accent, volume = 0.8 }: ClickOptions,
): ClickVoice {
  const oscillator = ctx.createOscillator()
  const gain = ctx.createGain()
  oscillator.type = 'square'
  oscillator.frequency.value = accent ? 1760 : 1175
  const peak = (accent ? 1 : 0.6) * volume
  gain.gain.setValueAtTime(0.0001, time)
  gain.gain.exponentialRampToValueAtTime(peak, time + 0.002)
  gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.045)
  oscillator.connect(gain)
  gain.connect(destination)
  oscillator.start(time)
  oscillator.stop(time + 0.05)
  oscillator.onended = () => gain.disconnect()
  return {
    time,
    cancel() {
      try {
        oscillator.stop(0)
      } catch {
        // Ya parado.
      }
      gain.disconnect()
    },
  }
}

/** Lleva la cuenta de los clics programados para poder cancelar los que no han sonado. */
export class ClickQueue {
  private voices: ClickVoice[] = []

  add(voice: ClickVoice): void {
    this.voices.push(voice)
  }

  /** Olvida los que ya sonaron. */
  prune(now: number): void {
    this.voices = this.voices.filter((voice) => voice.time + 0.1 > now)
  }

  cancelPending(now: number): number {
    const pending = this.voices.filter((voice) => voice.time >= now)
    pending.forEach((voice) => voice.cancel())
    this.voices = []
    return pending.length
  }

  /** Cancela los que suenan en `time` o después y conserva los anteriores. */
  cancelFrom(time: number): number {
    const limit = time - 1e-6
    const pending = this.voices.filter((voice) => voice.time >= limit)
    pending.forEach((voice) => voice.cancel())
    this.voices = this.voices.filter((voice) => voice.time < limit)
    return pending.length
  }
}
